import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import get from 'lodash/get';
import { Price } from '@grandvisionhq/common';
import { PaymentForm } from '../../../component/PaymentForm/PaymentForm.js';
import { usePaymentsUIContext } from '../../../context/PaymentsUI/context.js';
import { useGetAdditionalCartCost } from '../../../hooks/use-get-additional-cart-costs.js';
import { useGetCartPaymentMethods } from '../../../hooks/use-get-cart-payment-methods.js';
import { usePaymentMethodLabelsMap } from '../../../hooks/use-payment-method-labels-map.js';
import { PaymentMethod } from '../../../models/cart-payment-methods/cart-payment-methods.js';
import { getRestPaymentMethodName } from '../utils/get-payment-method-name.js';
import '@grandvisionhq/api-client';
import '../../../models/adyen/payment-type.js';
import '../../../models/googlePay/types.js';
import '../../../models/payment/types.js';
import '../../../models/paypal/context.js';
import { usePaymentMethodForm } from './use-payment-method-form.js';

var useGetPaymentMethodList = (cartId, onPaymentCompleteHandler, selectedDonation, onDonationSelected, totalPrice, googlePayActions)=>{
    const { titleLabels  } = usePaymentMethodLabelsMap();
    const [getPaymentMethods, queryResult] = useGetCartPaymentMethods();
    const { getAdditionalCosts , additionalCosts  } = useGetAdditionalCartCost();
    const { isProcessingOrder  } = usePaymentsUIContext();
    React.useEffect(()=>{
        if (cartId) {
            getPaymentMethods(cartId);
            getAdditionalCosts();
        }
    }, [
        cartId,
        getPaymentMethods,
        getAdditionalCosts
    ]);
    const [renderPaymentForm, selectedPaymentDetails, resetPaymentDetails] = usePaymentMethodForm(googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick, googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize);
    const paymentMethods = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.data;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.data
    ]);
    const error = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.error;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.error
    ]);
    const loading = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.loading
    ]);
    const called = React.useMemo(()=>{
        return queryResult === null || queryResult === void 0 ? void 0 : queryResult.called;
    }, [
        queryResult === null || queryResult === void 0 ? void 0 : queryResult.called
    ]);
    const selectedPaymentData = React.useMemo(()=>selectedPaymentDetails
    , [
        selectedPaymentDetails
    ]);
    const onHandlePaymentAction = React.useCallback(()=>{
        onPaymentCompleteHandler(selectedPaymentDetails);
    }, [
        onPaymentCompleteHandler,
        selectedPaymentDetails
    ]);
    const paymentMethodItems = React.useMemo(()=>{
        if (!paymentMethods || error) {
            return [];
        }
        const items = paymentMethods.map((_param)=>{
            var { provider , name , icons  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
                "provider",
                "name",
                "icons"
            ]);
            var ref, ref1;
            const mappedApiName = getRestPaymentMethodName(name);
            let item = React.Children.toArray(icons) // Call toArray so we can manipulate the data with type safety
            ;
            // Asides are provided as an array of thumbnail images for example.
            // Loop over additionalCosts to bind them to the aside array and return the combination.
            if (additionalCosts && additionalCosts.length) {
                additionalCosts.forEach((additionalCost, i)=>{
                    if (additionalCost.key === mappedApiName) {
                        const price = get(additionalCost, 'masterVariant.prices[0].value.centAmount', 0);
                        if (price) {
                            item = [
                                /*#__PURE__*/ React.createElement("span", {
                                    key: `${additionalCost.key}-aside-${i}`,
                                    className: "checkout-payment-methods__additional-cost"
                                }, "+ ", /*#__PURE__*/ React.createElement(Price, {
                                    value: price
                                })),
                                ...item !== null && item !== void 0 ? item : [], 
                            ];
                        }
                    }
                });
            }
            let formConfig;
            const paymentMethodList = (ref = rest) === null || ref === void 0 ? void 0 : ref.items;
            const cardBrands = (ref1 = rest) === null || ref1 === void 0 ? void 0 : ref1.brands;
            if (paymentMethodList) {
                formConfig = {
                    items: paymentMethodList
                };
            }
            if (cardBrands) {
                formConfig = {
                    brands: cardBrands
                };
            }
            if (name == PaymentMethod.GOOGLE_PAY && (googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick) && (googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize)) {
                formConfig = {
                    onClick: googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick,
                    onAuthorized: googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize
                };
            }
            const formComponent = renderPaymentForm({
                type: name,
                provider,
                config: formConfig
            });
            return {
                value: name,
                label: titleLabels[name],
                id: `${provider}-${name}`,
                disabled: isProcessingOrder,
                aside: item,
                content: /*#__PURE__*/ React.createElement(PaymentForm, swcHelpers.extends({
                    paymentMethod: name,
                    paymentProvider: provider,
                    form: formComponent,
                    onHandlePaymentAction: onHandlePaymentAction,
                    isActionDisabled: false,
                    isActionLoading: false,
                    selectedDonation: selectedDonation,
                    onDonationSelected: onDonationSelected,
                    totalPrice: totalPrice
                }, 'donations' in rest && {
                    donations: rest.donations
                }))
            };
        });
        return items;
    }, [
        paymentMethods,
        error,
        additionalCosts,
        renderPaymentForm,
        titleLabels,
        isProcessingOrder,
        onHandlePaymentAction,
        selectedDonation,
        onDonationSelected,
        totalPrice,
        googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.handleGpayCtaClick,
        googlePayActions === null || googlePayActions === void 0 ? void 0 : googlePayActions.onAuthorize, 
    ]);
    return [
        React.useMemo(()=>paymentMethodItems
        , [
            paymentMethodItems
        ]),
        selectedPaymentData,
        resetPaymentDetails,
        additionalCosts,
        {
            error,
            called,
            loading,
            data: paymentMethods || []
        }, 
    ];
};

export { useGetPaymentMethodList };
//# sourceMappingURL=use-get-payment-method-list.js.map
